import { useEffect, useState } from "react";
import { lighten, useTheme } from "@mui/material";
import { OmakasePlayer } from '@byomakase/omakase-player';
import { ContentPlaybackTimelineAudioTrack, CustomMarker, CustomMarkerStyle, FileWithProxy, PlayerElements } from "./video-player.types";
import { addAudioSidecar, addBlackSegmentsLane, addEnclosedCaptionLane, addExternalSubtitle, addPointsOfInterests, addThumbnails, createAudioTracks, createInOutMarkerLane, removeAllAudioSidecar } from "./video-player-utils";
import { OmakasePlayerClientConfig, OmakasePlayerFeature } from "@/__configs/config_types";
import { useConfigValue } from "@/components/_hooks/useConfigValue";

type UseVideoPlayerAdvancedParams = {
  customMarkers: CustomMarker[];
  focusedMarkerId?: string;
  customAudio?: FileWithProxy;
  onMarkerFocus?: (markerId: string | undefined) => void;
  externalSubtitles: FileWithProxy[];
  blackSegmentsVttUrl: string;
  pointsOfInterestVttUrl: string;
  thumbnailsVttUrl: string;
  audioTracks?: ContentPlaybackTimelineAudioTrack[];
};

const useVideoPlayerAdvanced = ({
  customMarkers,
  focusedMarkerId,
  customAudio,
  onMarkerFocus,
  externalSubtitles,
  blackSegmentsVttUrl,
  pointsOfInterestVttUrl,
  thumbnailsVttUrl,
  audioTracks,
}: UseVideoPlayerAdvancedParams) => {
  const config = useConfigValue<OmakasePlayerClientConfig>(['omakasePlayerConfig']);
  const theme = useTheme();
  const isLightMode = theme.palette.mode === 'light';
  const [player, setPlayer] = useState<OmakasePlayer | null>(null);
  const [lanes, setLanes] = useState<Map<string, Array<PlayerElements>>>(new Map());
  const [isVideoLoaded, setIsVideoLoaded] = useState<boolean>(false);
  const customMarkerStyle: CustomMarkerStyle = {
    backgroundFill: theme.palette.background.paper,
    leftBackgroundFill: theme.palette.background.default,
    labelTextColor: theme.palette.text.primary,
    markerSquareBackgroundFill: theme.palette.default.main,
    poiBackgroundFill: theme.palette.tertiary.main,
    darkTextColor: theme.palette.background.default,
    lightTextColor: lighten(theme.palette.text.primary, 0.7),
    success: theme.palette.success.main,
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
  };

  const updateLanes = (key: string, elements: PlayerElements[]) => {
    setLanes((prev) => {
      const newLanes = new Map(prev);
      newLanes.set(key, elements);
      return newLanes;
    });
  };

  const createTimeline = (omakasePlayer: OmakasePlayer | null) => {
    if (!omakasePlayer || !omakasePlayer.timeline) {
      return;
    }
    setPlayer(omakasePlayer);

    omakasePlayer.timeline?.onReady$?.subscribe(() => {
      const scrubberLane = omakasePlayer.timeline?.getScrubberLane();
      if (scrubberLane) {
        scrubberLane.style = {
          height: 80,
          backgroundFill: customMarkerStyle.backgroundFill,
          leftBackgroundFill: customMarkerStyle.leftBackgroundFill,
          descriptionTextFontSize: 20,
          marginBottom: 0,
          timecodeFill: customMarkerStyle.lightTextColor,
          tickFill: customMarkerStyle.lightTextColor,
        };

        scrubberLane.updateLayoutDimensions();
      }

      const otherLanes: PlayerElements[] = [];

      if (config?.features?.includes(OmakasePlayerFeature.CUSTOM_MARKERS)) {
        otherLanes.push(
          createInOutMarkerLane(omakasePlayer, customMarkers, customMarkerStyle, isLightMode, onMarkerFocus, focusedMarkerId) as PlayerElements,
        );
      }

      if (config?.features?.includes(OmakasePlayerFeature.BLACK_SEGMENTS) && blackSegmentsVttUrl?.length) {
        otherLanes.push(
          addBlackSegmentsLane(omakasePlayer, blackSegmentsVttUrl, customMarkerStyle),
        );
      }

      if (config?.features?.includes(OmakasePlayerFeature.POINTS_OF_INTEREST) && pointsOfInterestVttUrl?.length) {
        otherLanes.push(
          addPointsOfInterests(omakasePlayer, pointsOfInterestVttUrl, customMarkerStyle),
        );
      }

      if (config?.features?.includes(OmakasePlayerFeature.THUMBNAILS) && thumbnailsVttUrl?.length) {
        otherLanes.push(
          addThumbnails(omakasePlayer, thumbnailsVttUrl, customMarkerStyle) as PlayerElements,
        );
      }

      if (otherLanes.length) {
        updateLanes('others', otherLanes);
      }

      let addedDefaultSubtitle = false;

      omakasePlayer.subtitles.onSubtitlesLoaded$.subscribe(async () => {
        const [subtitlesVttTracks] = omakasePlayer.subtitles.getTracks();
        if (!addedDefaultSubtitle && subtitlesVttTracks) {
          addedDefaultSubtitle = true;
          if (config?.features?.includes(OmakasePlayerFeature.SUBTITLES)) {
            const embeddedSubtitle = await addEnclosedCaptionLane(omakasePlayer, {
              id: subtitlesVttTracks.id,
              name: 'Embedded CC',
              lang: subtitlesVttTracks.language?.toUpperCase(),
              code: subtitlesVttTracks.language?.toUpperCase(),
              url: subtitlesVttTracks.src,
            }, true, customMarkerStyle);
            updateLanes('subtitle', [embeddedSubtitle] as PlayerElements[]);
          }
        }
      });

    });

    return lanes;
  };

  const onSubtitlesChanged = async () => {
    if (player && isVideoLoaded) {
      try {
        const externalSubtitleIds = new Set(
          externalSubtitles
            .map((subtitle) => subtitle.id?.toString())
            .filter((id): id is string => Boolean(id)),
        );

        const removedExternalTracks = player.subtitles
          .getTracks()
          .filter((track) => !track.embedded && !externalSubtitleIds.has(track.id));

        if (removedExternalTracks.length) {
          removedExternalTracks.forEach((track) => {
            if (player.timeline?.getTimelineLane(track.id)) {
              player.timeline.removeTimelineLane(track.id);
            }
          });

          await Promise.all(
            removedExternalTracks.map((track) => {
              return new Promise<void>((resolve, reject) => {
                player.subtitles.removeTrack(track.id).subscribe({
                  next: () => resolve(),
                  error: (error) => reject(error),
                });
              });
            }),
          );
        }

        const subtitles = await Promise.all(externalSubtitles.map((sub) => {
          return addExternalSubtitle(player, sub, false, customMarkerStyle);
        }));

        updateLanes('subtitle', subtitles as PlayerElements[]);
      } catch (error) {
        console.error('Error loading external subtitles:', error);
      }
    }
  }

  useEffect(() => {
    if (player && config?.features?.includes(OmakasePlayerFeature.CUSTOM_MARKERS)) {
      const markerLane = createInOutMarkerLane(player, customMarkers, customMarkerStyle, isLightMode, onMarkerFocus, focusedMarkerId) as PlayerElements;
      setLanes((prev) => {
        const newLanes = new Map(prev);
        const others = newLanes.get('others') || [];
        const newOthers = others.length > 0 ? [markerLane, ...others.slice(1)] : [markerLane];
        newLanes.set('others', newOthers);
        return newLanes;
      });
    }
  }, [customMarkers, focusedMarkerId, player, config, onMarkerFocus]);

  useEffect(() => {
    if (player && audioTracks?.length && config?.features?.includes(OmakasePlayerFeature.AUDIO_TRACKS)) {
      updateLanes('audio', createAudioTracks(player, audioTracks, customMarkerStyle, isLightMode));
    }
  }, [player, audioTracks, config]);

  useEffect(() => {
    if (!player || !config?.features?.includes(OmakasePlayerFeature.AUDIO_TRACKS)) return;
    if (customAudio) {
      addAudioSidecar(player, customAudio.proxy.url);
    } else {
      removeAllAudioSidecar(player);
    }
  }, [player, customAudio, config]);

  useEffect(() => {
    setIsVideoLoaded(false);

    if (!player) return;

    const subscription = player.video.onVideoLoaded$.subscribe({
      next: (event) => {
        if (event) {
          setIsVideoLoaded(true);
        }
      },
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [player]);

  useEffect(() => {
    if (isVideoLoaded) {
      onSubtitlesChanged();
    }
  }, [isVideoLoaded, externalSubtitles]);

  return {
    createTimeline,
    lanes,
  };
};

export default useVideoPlayerAdvanced;